/**
 * MusicService - Fetches song metadata from MusicBrainz API
 *
 * MusicBrainz is a free, open music encyclopedia
 * API Documentation: https://musicbrainz.org/doc/MusicBrainz_API
 *
 * Provides metadata:
 * - Song Title
 * - Artist
 * - Composer
 * - Album/Show (for musicals)
 * - Year of publication/composition
 * - Genre
 */

const MUSICBRAINZ_API_BASE = 'https://musicbrainz.org/ws/2';
const APP_NAME = 'CYVWebsite';
const APP_VERSION = '1.0.0';
const CONTACT = 'your-email@example.com'; // Update with your email

/**
 * Scoring helpers to pick the best release (prefer cast albums and soundtracks)
 */
const CAST_KEYWORDS = [
	'original broadway cast',
	'cast recording',
	'obcr',
	'london cast',
	'west end cast',
	'studio cast',
	'broadway cast',
	'new broadway cast',
];

const SOUNDTRACK_KEYWORDS = [
	'motion picture',
	'movie soundtrack',
	'film soundtrack',
	'soundtrack',
	'motion picture soundtrack',
];

const DEMO_OR_WORKSHOP = ['workshop', 'demo'];

/**
 * Year from a MusicBrainz date, WITHOUT `new Date()`.
 *
 * MusicBrainz dates are partial constantly — "2003" and "1996" are as common
 * as full dates. `new Date('2003')` parses as 2003-01-01T00:00:00**Z**, so
 * `.getFullYear()` in any timezone west of UTC returns **2002**. Measured in
 * America/Chicago, which is where this studio is: every year-only date was
 * rendering one year early on the live song-search rows.
 *
 * The leading four characters are the year in every format MusicBrainz emits
 * (YYYY, YYYY-MM, YYYY-MM-DD), so slicing is both simpler and correct.
 */
export const parseYear = (date) => {
	const year = String(date || '').slice(0, 4);
	return /^\d{4}$/.test(year) ? Number(year) : null;
};

// Exported so the Add-a-song ranking can reuse THIS vocabulary rather than
// keeping a second copy of the keyword lists. Two lists would drift, and the
// one that rotted would silently stop surfacing cast recordings.
export const inferRecordingType = (title = '') => {
	const lower = title.toLowerCase();
	if (CAST_KEYWORDS.some((kw) => lower.includes(kw))) return 'Cast Recording';
	if (SOUNDTRACK_KEYWORDS.some((kw) => lower.includes(kw))) return 'Soundtrack';
	if (DEMO_OR_WORKSHOP.some((kw) => lower.includes(kw))) return 'Workshop/Demo';
	return 'Album';
};

const keywordMatchScore = (title = '', keywords = [], scoreValue = 1) => {
	const lower = title.toLowerCase();
	return keywords.some((kw) => lower.includes(kw)) ? scoreValue : 0;
};

const scoreRelease = (release, showHint = '') => {
	let score = 0;
	const title = release?.title || '';

	if (release?.status && release.status.toLowerCase() === 'official') {
		score += 2;
	}

	score += keywordMatchScore(title, CAST_KEYWORDS, 6);
	score += keywordMatchScore(title, SOUNDTRACK_KEYWORDS, 4);
	score -= keywordMatchScore(title, DEMO_OR_WORKSHOP, 2);

	if (showHint && title.toLowerCase().includes(showHint.toLowerCase())) {
		score += 2;
	}

	return score;
};

const pickPreferredRelease = (releases = [], showHint = '') => {
	if (!releases || releases.length === 0) return null;
	if (releases.length === 1) return releases[0];

	const scored = releases.map((release) => ({
		release,
		score: scoreRelease(release, showHint),
		date: release.date ? new Date(release.date) : null,
	}));

	scored.sort((a, b) => {
		if (b.score !== a.score) return b.score - a.score;
		if (a.date && b.date) return b.date - a.date; // prefer newer if scores tie
		return 0;
	});

	return scored[0].release;
};

/**
 * Search for song metadata by title and optional artist
 * Returns the best single match (first option)
 */
export const searchSongMetadata = async (songTitle, artistName = '') => {
	const options = await searchSongMetadataOptions(songTitle, artistName, 1);
	return options?.[0] || null;
};

/**
 * A list-grade option built from a SEARCH result — no follow-up request.
 *
 * MEASURED 2026-08-02, and this is the whole basis of the details-on-pick
 * change: the `/recording?query=` payload already carries `artist-credit`,
 * `releases` (title + date), `first-release-date` and `length`. The old code
 * read `.id` off each result, threw the rest away, and re-fetched it one
 * recording at a time — 20 requests for a 12-result search, of which 7 came
 * back 503 because the burst blew MusicBrainz's 1-req/sec contract.
 *
 * WHAT A SEARCH RESULT DOES *NOT* CARRY is `relations` — the work-rels that
 * yield composer and lyricist — plus genres/tags. So these rows are
 * deliberately CREDIT-FREE, marked `detailsFetched: false`, and hydrated by
 * `fetchRecordingDetails` when a teacher picks one. That is not a loss of
 * information the list was using: credits are identical across every cover of
 * a song, so they never helped choose BETWEEN rows, and Apple and Discogs rows
 * never carried them in the list either.
 *
 * `release-group.secondary-types` is folded into the recording-type inference
 * because MusicBrainz types 'Soundtrack' explicitly there — a stronger signal
 * than matching words in an album title.
 */
export const buildOptionFromSearchResult = (recording = {}) => {
	const releases = recording.releases || [];
	const preferredRelease = pickPreferredRelease(releases, recording.title);
	const release = preferredRelease || releases[0] || null;
	const albumTitle = release?.title || '';
	const secondaryTypes = release?.['release-group']?.['secondary-types'] || [];

	return {
		title: recording.title || '',
		artist: recording['artist-credit']?.[0]?.name || '',
		artistId: recording['artist-credit']?.[0]?.artist?.id || '',
		// Empty by construction — see the docblock. Hydrated on pick.
		composers: [],
		lyricists: [],
		arrangers: [],
		album: albumTitle,
		recordingType: inferRecordingType([albumTitle, ...secondaryTypes].join(' ')),
		releaseYear: parseYear(release?.date || recording['first-release-date']),
		genres: [],
		tags: [],
		musicbrainzId: recording.id || null,
		recordingId: recording.id || null,
		length: recording.length ? Math.floor(recording.length / 1000) : null,
		releaseLabel: '',
		releaseDisambiguation: release?.disambiguation || '',
		isMusical: false,
		musicalName: '',
		yearWritten: null,
		workId: null,
		source: 'musicbrainz',
		// Lets the pick-time hydrator tell a list row from an already-complete
		// one, so re-picking the same row does not re-fetch.
		detailsFetched: false,
	};
};

/**
 * Return multiple possible recordings so the user can choose
 * @param {string} songTitle
 * @param {string} artistName
 * @param {number} limit - max results to return
 * @returns {Promise<Array>} Array of metadata options
 */
export const searchSongMetadataOptions = async (songTitle, artistName = '', limit = 12) => {
	try {
		let query = `recording:"${songTitle}"`;
		if (artistName) {
			query += ` AND artist:"${artistName}"`;
		}

		const response = await fetch(
			`${MUSICBRAINZ_API_BASE}/recording?query=${encodeURIComponent(query)}&fmt=json&limit=${limit}`,
			{
				headers: {
					'User-Agent': `${APP_NAME}/${APP_VERSION} ( ${CONTACT} )`,
				},
			}
		);

		if (!response.ok) {
			throw new Error(`MusicBrainz API error: ${response.status}`);
		}

		const data = await response.json();

		if (!data.recordings || data.recordings.length === 0) {
			return [];
		}

		// ONE request, not one-per-result. See buildOptionFromSearchResult: the
		// search payload already carries everything a list row renders, and the
		// per-recording detail fetch that used to live here is now deferred to
		// the moment a teacher PICKS a row (fetchRecordingDetails).
		return data.recordings.map(buildOptionFromSearchResult);
	} catch (error) {
		console.error('Error fetching song metadata options:', error);
		return [];
	}
};

/**
 * Get detailed recording information including releases and credits
 * @param {string} recordingId - MusicBrainz recording ID
 * @returns {Promise<Object>} Detailed song metadata
 */
export const getRecordingDetails = async (recordingId) => {
	try {
		const response = await fetch(
			`${MUSICBRAINZ_API_BASE}/recording/${recordingId}?inc=artist-credits+releases+genres+tags+work-rels&fmt=json`,
			{
				headers: {
					'User-Agent': `${APP_NAME}/${APP_VERSION} ( ${CONTACT} )`,
				},
			}
		);

		if (!response.ok) {
			throw new Error(`MusicBrainz API error: ${response.status}`);
		}

		const recording = await response.json();

		// Extract metadata
		const preferredRelease = pickPreferredRelease(
			recording.releases,
			recording.title
		);

		const metadata = {
			title: recording.title || '',
			artist: recording['artist-credit']?.[0]?.name || '',
			artistId: recording['artist-credit']?.[0]?.artist?.id || '',
			composers: [],
			lyricists: [],
			arrangers: [],
			album: preferredRelease?.title || recording.releases?.[0]?.title || '',
			recordingType: inferRecordingType(preferredRelease?.title || recording.releases?.[0]?.title || ''),
			// parseYear, not `new Date(...).getFullYear()` — a year-only
			// MusicBrainz date read one year early west of UTC. See parseYear.
			releaseYear:
				parseYear(preferredRelease?.date) ??
				parseYear(recording.releases?.[0]?.date),
			genres: recording.genres?.map(g => g.name) || [],
			tags: recording.tags?.map(t => t.name) || [],
			musicbrainzId: recording.id,
			recordingId: recording.id,
			length: recording.length ? Math.floor(recording.length / 1000) : null, // Convert to seconds
			releaseLabel: preferredRelease?.['label-info']?.[0]?.label?.name || recording.releases?.[0]?.['label-info']?.[0]?.label?.name || '',
			releaseDisambiguation: preferredRelease?.disambiguation || recording.disambiguation || '',
			musicalName: '',
			yearWritten: null,
			workId: null,
		};

		// Check if it's from a musical (look for musical theater tags)
		const musicalTags = metadata.tags.filter(tag =>
			tag.toLowerCase().includes('musical') ||
			tag.toLowerCase().includes('theater') ||
			tag.toLowerCase().includes('theatre') ||
			tag.toLowerCase().includes('broadway')
		);

		if (musicalTags.length > 0 || metadata.album.toLowerCase().includes('musical')) {
			metadata.isMusical = true;
			metadata.musicalName = metadata.album;
		}

		// Prefer composer/lyricist/arranger relationships from the underlying work (most reliable for musicals)
		const workRelation = (recording.relations || []).find(rel => rel.work);
		if (workRelation?.work?.id) {
			const workInfo = await getWorkDetails(workRelation.work.id);
			metadata.workId = workRelation.work.id;
			if (workInfo.composers.length > 0) {
				metadata.composers = workInfo.composers;
			}
			if (workInfo.lyricists.length > 0) {
				metadata.lyricists = workInfo.lyricists;
			}
			if (workInfo.arrangers.length > 0) {
				metadata.arrangers = workInfo.arrangers;
			}
			// Prefer work title/year when present (often the show name for musicals)
			if (workInfo.musicalName && !metadata.musicalName) {
				metadata.isMusical = true;
				metadata.musicalName = workInfo.musicalName;
			}
			if (workInfo.yearWritten) {
				metadata.yearWritten = workInfo.yearWritten;
			}
		}

		// NO FALLBACK HERE, DELIBERATELY. There used to be one: when work-rels
		// yielded no composer it called getArtistWorks(artistId), which fetched
		// /artist/{id}?inc=works, DISCARDED the works, and returned the artist's
		// own name — asserting the PERFORMER is the composer.
		//
		// Observed live 2026-08-02: picking "Tomorrow" from Annie (Original
		// Broadway Cast Recording) attributed it to MARTY ROBBINS. For a
		// musical-theatre studio that is wrong nearly every time — Andrea McArdle
		// sang "Tomorrow", Charles Strouse wrote it — and the wrong name flowed
		// into the song header and from there into Song Notes.
		//
		// An empty composer field is honest and visibly needs filling in. A
		// confident wrong one does not, so a teacher has no reason to doubt it.
		// If no work relation carries a composer, we say nothing.

		return metadata;
	} catch (error) {
		console.error('Error fetching recording details:', error);
		return null;
	}
};

/**
 * Get detailed work information (to pull composer/lyricist credits)
 * @param {string} workId - MusicBrainz work ID
 * @returns {Promise<Object>} Work metadata including composers
 */
const getWorkDetails = async (workId) => {
	try {
		const response = await fetch(
			`${MUSICBRAINZ_API_BASE}/work/${workId}?inc=artist-rels+aliases+tags&fmt=json`,
			{
				headers: {
					'User-Agent': `${APP_NAME}/${APP_VERSION} ( ${CONTACT} )`,
				},
			}
		);

		if (!response.ok) {
			return { composers: [] };
		}

		const work = await response.json();

		// Credits are stored as artist relations on the work
		const composerRelations = (work.relations || []).filter(rel =>
			['composer', 'writer'].includes((rel.type || '').toLowerCase())
		);
		const lyricistRelations = (work.relations || []).filter(rel =>
			['lyricist', 'librettist'].includes((rel.type || '').toLowerCase())
		);
		const arrangerRelations = (work.relations || []).filter(rel =>
			['arranger', 'orchestrator'].includes((rel.type || '').toLowerCase())
		);

		const composers = composerRelations.map(rel => rel.artist?.name).filter(Boolean);
		const lyricists = lyricistRelations.map(rel => rel.artist?.name).filter(Boolean);
		const arrangers = arrangerRelations.map(rel => rel.artist?.name).filter(Boolean);

		// Tags often reveal musicals
		const musicalName = work.title;
		const yearWritten = work['first-release-date']
			? new Date(work['first-release-date']).getFullYear()
			: null;

		return {
			composers,
			lyricists,
			arrangers,
			musicalName,
			yearWritten,
		};
	} catch (error) {
		console.error('Error fetching work details:', error);
		return { composers: [] };
	}
};

// getArtistWorks was REMOVED 2026-08-02 along with its only caller. It asked
// MusicBrainz for /artist/{id}?inc=works, ignored the works it had just paid a
// request for, and returned the artist's own name as the composer. That was
// both a wasted round trip and a source of confidently wrong credits — see the
// note in getRecordingDetails where it used to be called.

/**
 * Search for musical theater show information
 * @param {string} showName - Name of the musical
 * @returns {Promise<Object|null>} Musical metadata
 */
export const searchMusicalMetadata = async (showName) => {
	try {
		const response = await fetch(
			`${MUSICBRAINZ_API_BASE}/release-group?query=releasegroup:"${encodeURIComponent(showName)}" AND type:album&fmt=json&limit=5`,
			{
				headers: {
					'User-Agent': `${APP_NAME}/${APP_VERSION} ( ${CONTACT} )`,
				},
			}
		);

		if (!response.ok) {
			throw new Error(`MusicBrainz API error: ${response.status}`);
		}

		const data = await response.json();

		if (!data['release-groups'] || data['release-groups'].length === 0) {
			return null;
		}

		const releaseGroup = data['release-groups'][0];

		return {
			musicalName: releaseGroup.title,
			yearWritten: releaseGroup['first-release-date']
				? new Date(releaseGroup['first-release-date']).getFullYear()
				: null,
			composers: releaseGroup['artist-credit']?.map(ac => ac.name) || [],
			musicbrainzId: releaseGroup.id,
		};
	} catch (error) {
		console.error('Error fetching musical metadata:', error);
		return null;
	}
};

/**
 * Format metadata for display
 * @param {Object} metadata - Song metadata object
 * @returns {string} Formatted string for display
 */
export const formatMetadataDisplay = (metadata) => {
	if (!metadata) return 'No metadata available';

	let display = `${metadata.title}`;

	if (metadata.artist) {
		display += ` by ${metadata.artist}`;
	}

	if (metadata.composers && metadata.composers.length > 0) {
		display += ` (${metadata.composers.join(', ')})`;
	}

	if (metadata.album) {
		display += ` from ${metadata.album}`;
	}

	if (metadata.releaseYear) {
		display += ` (${metadata.releaseYear})`;
	}

	if (metadata.genres && metadata.genres.length > 0) {
		display += ` - ${metadata.genres.join(', ')}`;
	}

	return display;
};

/**
 * Respect MusicBrainz rate limiting (1 request per second)
 */
let lastRequestTime = 0;
const RATE_LIMIT_MS = 1000;

const rateLimit = () => {
	return new Promise((resolve) => {
		const now = Date.now();
		const timeSinceLastRequest = now - lastRequestTime;

		if (timeSinceLastRequest < RATE_LIMIT_MS) {
			setTimeout(() => {
				lastRequestTime = Date.now();
				resolve();
			}, RATE_LIMIT_MS - timeSinceLastRequest);
		} else {
			lastRequestTime = now;
			resolve();
		}
	});
};

// Wrap API calls with rate limiting
const originalSearchSongMetadata = searchSongMetadata;
export const searchSongMetadataWithRateLimit = async (...args) => {
	await rateLimit();
	return originalSearchSongMetadata(...args);
};

const originalSearchSongMetadataOptions = searchSongMetadataOptions;
export const searchSongMetadataOptionsWithRateLimit = async (...args) => {
	await rateLimit();
	return originalSearchSongMetadataOptions(...args);
};

/**
 * Fetch a "strict" credits match: prefers entries with composer/lyricist data.
 */
export const fetchSongCreditsStrict = async (songTitle, artistName = '', limit = 8) => {
	const options = await searchSongMetadataOptionsWithRateLimit(songTitle, artistName, limit);
	if (!options || options.length === 0) return null;

	// Score: composer/lyricist presence > musical tag > recency
	const scored = options.map((opt) => {
		const hasCredits = (opt.composers && opt.composers.length) || (opt.lyricists && opt.lyricists.length);
		const isMusical = opt.isMusical || (opt.musicalName && opt.musicalName.length > 0);
		const year = opt.releaseYear || opt.yearWritten || 0;
		let score = 0;
		if (hasCredits) score += 5;
		if (isMusical) score += 2;
		score += Math.min(year / 10000, 1); // slight preference for dated items
		return { opt, score };
	});

	scored.sort((a, b) => b.score - a.score);
	return scored[0].opt;
};

export const fetchSongCreditsStrictWithRateLimit = async (...args) => {
	await rateLimit();
	return fetchSongCreditsStrict(...args);
};

/**
 * Pick-time hydration: the full detail lookup for ONE recording the teacher
 * actually chose. This is the call that used to run 12 times per search, in a
 * loop, ungated. Now it runs once, gated, after a decision — so the 1-req/sec
 * contract costs a single second instead of blowing up into 503s.
 */
export const fetchRecordingDetailsWithRateLimit = async (recordingId) => {
	await rateLimit();
	return getRecordingDetails(recordingId);
};

export default {
	searchSongMetadata,
	searchSongMetadataWithRateLimit,
	searchSongMetadataOptions,
	searchSongMetadataOptionsWithRateLimit,
	fetchSongCreditsStrict,
	fetchSongCreditsStrictWithRateLimit,
	searchMusicalMetadata,
	formatMetadataDisplay,
};
